export const STEP_ORDER = [
  'intake',
  'merchant',
  'locations',
  'practitioners',
  'customer-account',
  'contract',
  'fee-schedule',
  'csps',
  'review',
  'done',
] as const;

export type StepId = (typeof STEP_ORDER)[number];

export const STEP_LABELS: Record<StepId, string> = {
  intake: 'Intake',
  merchant: 'Merchant',
  locations: 'Locations',
  practitioners: 'Practitioners',
  'customer-account': 'Customer account',
  'fee-schedule': 'Fee schedule',
  contract: 'Contract',
  csps: 'Service providers',
  review: 'Review & commit',
  done: 'Done',
};

export const stepIndex = (id: StepId): number => STEP_ORDER.indexOf(id);

export const nextStep = (id: StepId): StepId | undefined =>
  STEP_ORDER[stepIndex(id) + 1];

export const prevStep = (id: StepId): StepId | undefined =>
  STEP_ORDER[stepIndex(id) - 1];

export interface StepPhase {
  id: 'setup' | 'network' | 'financial' | 'wrap-up';
  label: string;
  steps: readonly StepId[];
}

// Phases group the steps into cognitive chunks for the top nav.
// Order here defines display order; steps within a phase must be a contiguous
// slice of STEP_ORDER for the "current phase" math to work.
export const STEP_PHASES: readonly StepPhase[] = [
  { id: 'setup', label: 'Setup', steps: ['intake'] },
  {
    id: 'network',
    label: 'Network',
    steps: ['merchant', 'locations', 'practitioners'],
  },
  {
    id: 'financial',
    label: 'Financial',
    steps: ['customer-account', 'contract', 'fee-schedule'],
  },
  { id: 'wrap-up', label: 'Wrap-up', steps: ['csps', 'review', 'done'] },
] as const;

export const phaseOfStep = (id: StepId): StepPhase => {
  const found = STEP_PHASES.find(p =>
    (p.steps as readonly StepId[]).includes(id),
  );
  if (found == null) {
    throw new Error(`No phase configured for step "${id}"`);
  }
  return found;
};

// Bumped to `:v2` when Contract was moved ahead of FeeSchedule and the
// ContractDecision shape changed to a discriminated union. Old persisted blobs
// fail JSON.parse cleanly and fall back to empty.
export const WIZARD_STORAGE_KEY = 'hingeSelect:contracts:rawInput:v2';
